'use client';

import cx from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';

import { DownloadAppModal } from '@/components/DownloadAppModal';
import { LoginModal } from '@/components/LoginModal';
import { ROUTES } from '@/constants';
import { useCustomAuth } from '@/contexts/AuthContext';
import { useFetchNotifications } from '@/hooks/useFetchNotifications';

import { NotificationsModal } from '../NotificationsModal';
import styles from './Header.module.scss';
import { EHeaderType } from './Header.types';
import { HDesktop } from './subcomponents/Desktop.component';
import { HMobile } from './subcomponents/Mobile.component';

function getHeaderTypeByPathname(pathname: string) {
  switch (pathname) {
    case ROUTES.HOME:
      return EHeaderType.HOME;
    case ROUTES.PROFILE:
      return EHeaderType.USER;
    case ROUTES.SEARCH:
      return EHeaderType.SEARCH;
    default:
      return EHeaderType.DEFAULT;
  }
}

const HeaderWithSearchParams = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] =
    useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [enableNotificationsFetch, setEnableNotificationsFetch] =
    useState(false);

  const openModal = () => setIsModalOpen(true);

  const type = getHeaderTypeByPathname(pathname);

  const hiddenRoutes = [
    ROUTES.TERMS_OF_SERVICE,
    ROUTES.DATA_PRIVACY,
    ROUTES.INTERESTS,
  ];

  const normalizedPathname = pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  const isHiddenRoute = hiddenRoutes.some((route) =>
    normalizedPathname.startsWith(route),
  );

  const auth = useCustomAuth();

  // Defer notifications fetch to reduce initial script execution time
  useEffect(() => {
    if (auth.isAuthenticated && !enableNotificationsFetch) {
      // Enable fetch after 2 seconds (after critical rendering is done)
      const timer = setTimeout(() => {
        setEnableNotificationsFetch(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [auth.isAuthenticated, enableNotificationsFetch]);

  const {
    unreadNotifications,
    notifications,
    hasNextPage,
    fetchNextPage,
    isFetching,
    refetch,
  } =
    useFetchNotifications({
      isAuth: auth.isAuthenticated,
      enableFetch: enableNotificationsFetch,
    }) || {};

  return (
    <section
      className={cx(styles.header, {
        [styles['hide-row']]: isHiddenRoute,
        [styles.home]: type === EHeaderType.HOME,
        [styles.float]: pathname.includes(ROUTES.FEED),
      })}
    >
      <HDesktop openModal={openModal} />
      <HMobile
        openModal={() => {
          if (!auth.isAuthenticated) {
            setIsLoginModalOpen(true);
          } else {
            setIsNotificationsModalOpen(true);
          }
        }}
        unreadNotifications={unreadNotifications ?? 0}
      />
      <NotificationsModal
        isOpen={isNotificationsModalOpen}
        setIsOpen={setIsNotificationsModalOpen}
        notifications={notifications}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetching={isFetching}
        refetch={refetch}
      />
      <DownloadAppModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      <LoginModal
        isOpen={isLoginModalOpen}
        setIsOpen={setIsLoginModalOpen}
        urlBeforeLogin={`${pathname}${searchParams?.toString() ? `?${searchParams.toString()}` : ''}`}
      />
    </section>
  );
};

export const Header = () => {
  return (
    <Suspense fallback={null}>
      <HeaderWithSearchParams />
    </Suspense>
  );
};
